import { createElement, type SVGAttributes } from 'react';

const iconProps: SVGAttributes<SVGSVGElement> = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 20 20',
  fill: 'none',
  width: 16,
  height: 16,
  'aria-hidden': true,
  className: 'block shrink-0 text-fg-muted',
};

const stroke = {
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function SidebarPageBlankIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M6 2.75h6.88L16.25 6v11.25A1.75 1.75 0 0 1 14.5 19h-9A1.75 1.75 0 0 1 3.75 17.25V4.5A1.75 1.75 0 0 1 5.5 2.75H6Z',
    }),
    createElement('path', {
      ...stroke,
      d: 'M12.25 2.75V6a1.25 1.25 0 0 0 1.25 1.25H16.5',
    }),
  );
}

export function SidebarPageWithChildrenIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M6 2.75h6.88L16.25 6v11.25A1.75 1.75 0 0 1 14.5 19h-9A1.75 1.75 0 0 1 3.75 17.25V4.5A1.75 1.75 0 0 1 5.5 2.75H6Z',
    }),
    createElement('path', {
      ...stroke,
      d: 'M12.25 2.75V6a1.25 1.25 0 0 0 1.25 1.25H16.5',
    }),
    createElement('path', { ...stroke, d: 'M7 10h6' }),
    createElement('path', { ...stroke, d: 'M7 13h6' }),
    createElement('path', { ...stroke, d: 'M7 16h4' }),
  );
}

export function SidebarDatabaseIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('rect', { ...stroke, x: 3.75, y: 3, width: 12.5, height: 14, rx: 1.5 }),
    createElement('path', { ...stroke, d: 'M3.75 7.75h12.5' }),
    createElement('path', { ...stroke, d: 'M3.75 12.25h12.5' }),
    createElement('path', { ...stroke, d: 'M8.5 3v14' }),
  );
}

export function SidebarChevronIcon({
  expanded = false,
  className,
}: {
  expanded?: boolean;
  className?: string;
}) {
  return createElement(
    'svg',
    {
      ...iconProps,
      className: `${iconProps.className ?? ''} transition-transform duration-150 ${
        expanded ? 'rotate-90' : ''
      } ${className ?? ''}`.trim(),
    },
    createElement('path', {
      ...stroke,
      d: 'M8 5l4 5-4 5',
    }),
  );
}

export function SidebarMoreIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('circle', { cx: 5, cy: 10, r: 1.25, fill: 'currentColor', stroke: 'none' }),
    createElement('circle', { cx: 10, cy: 10, r: 1.25, fill: 'currentColor', stroke: 'none' }),
    createElement('circle', { cx: 15, cy: 10, r: 1.25, fill: 'currentColor', stroke: 'none' }),
  );
}

export function SidebarPlusIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M10 5.5v9' }),
    createElement('path', { ...stroke, d: 'M5.5 10h9' }),
  );
}

export function SidebarLibraryIcon() {
  const libraryStroke = { ...stroke, strokeWidth: 2 };
  return createElement(
    'svg',
    { ...iconProps, viewBox: '0 0 24 24' },
    createElement('path', { ...libraryStroke, d: 'm2 4 3 2v11H2z' }),
    createElement('path', { ...libraryStroke, d: 'm7 6 3 2v9H7z' }),
    createElement('path', { ...libraryStroke, d: 'm12 8 3 2v7h-3z' }),
  );
}

export function SidebarFinanceIcon() {
  const walletStroke = { ...stroke, strokeWidth: 2 };
  return createElement(
    'svg',
    { ...iconProps, viewBox: '0 0 24 24' },
    createElement('path', {
      ...walletStroke,
      d: 'M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1H5a2 2 0 0 0-2-2Z',
    }),
    createElement('path', {
      ...walletStroke,
      d: 'M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H5a2 2 0 0 1-2-2Z',
    }),
    createElement('circle', { cx: 16.5, cy: 13, r: 1.25, fill: 'currentColor', stroke: 'none' }),
  );
}

export function SidebarTrashIcon() {
  const trashStroke = { ...stroke, strokeWidth: 2 };
  return createElement(
    'svg',
    { ...iconProps, viewBox: '0 0 24 24' },
    createElement('path', { ...trashStroke, d: 'M10 11v6' }),
    createElement('path', { ...trashStroke, d: 'M14 11v6' }),
    createElement('path', { ...trashStroke, d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' }),
    createElement('path', { ...trashStroke, d: 'M3 6h18' }),
    createElement('path', { ...trashStroke, d: 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }),
  );
}

export function SidebarSettingsIcon() {
  return createElement(
    'svg',
    { ...iconProps, viewBox: '0 0 24 24' },
    createElement('path', {
      ...stroke,
      strokeWidth: 2,
      d: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
    }),
    createElement('circle', { ...stroke, strokeWidth: 2, cx: 12, cy: 12, r: 3 }),
  );
}
